import axios from "axios";
import { config, getJsConfig } from "./lib/configHelper";
import { setupGoogleTag, setupYandexTag } from "./helpers";

interface PageData {
  html: string;
  css?: string;
}

// Landings served by the platform carry this marker and are edited through the
// MCP connector (CO-15430). Opening one here would silently fork it: the editor
// rewrites the whole document on save, and the connector would keep editing the
// page it no longer controls.
const AI_MANAGED_MARKER = "meta[name='scrile-landing']";
const AI_MANAGED_MESSAGE =
  "This page is managed through the AI connector and cannot be edited here. Ask your AI assistant to change it, or delete the page in the admin panel to go back to the default landing.";

export default function (editor) {
  let aiManaged = false;

  const refuse = () => {
    editor.Modal.open({ title: "Editing is not available", content: AI_MANAGED_MESSAGE });
    return new Error(AI_MANAGED_MESSAGE);
  };

  editor.StorageManager.add("remote-file", {
    load: async (keys: string[], done: (data: PageData) => void, onError: (e: Error) => void) => {
      try {
        const url = editor.StorageManager.getConfig().urlLoad;
        let htmlText = "";
        try {
          await Promise.all([
            axios.get<string>(url).then(res => htmlText = res.data),
            getJsConfig(editor.StorageManager.getConfig().urlStore)
          ])
        } catch (e) {
          console.error(e);
        }

        let [htmlStart, content, htmlEnd] = [
          "<html><head><base href='/static/' /></head><body>",
          "",
          "</body></html>",
        ];
        const m = htmlText.match(/(.*<body>)(.*)(<\/body>.*)/is);
        if (m) [, htmlStart, content, htmlEnd] = m;

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlStart + content + htmlEnd, "text/html");

        aiManaged = !!doc.head.querySelector(AI_MANAGED_MARKER);
        if (aiManaged) {
          onError(refuse());
          return;
        }

        const canvasDoc = editor.Canvas.getDocument();
        canvasDoc.head.innerHTML = doc.head.innerHTML;

        editor.trigger("storage:loaded", { html: content });

        done({
          html: content,
        });
      } catch (e) {
        onError(e);
      }
    },
    store: async (data: PageData, clb: () => void, clbErr: (e: Error) => void) => {
      try {
        if (aiManaged) {
          clbErr(refuse());
          return;
        }

        const enabledHideLink = !!config?.featureHideScrileLink
        const isLanding = new URL(editor.StorageManager.getConfig().urlStore).pathname === "/static/index.html"
        
        const canvasDoc = editor.Canvas.getDocument();
        const hasBase = canvasDoc.head.querySelector("base:not([data-do-not-store])");
        const hasYandex = canvasDoc.head.querySelector("#yandex-tag");
        const hasGoogleScrile = canvasDoc.head.querySelector("#google-scrile-tag");
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          `<html><head>${canvasDoc.head.innerHTML}</head><body><style>${data.css ?? ""}</style>${
            data.html
          }</body></html>`,
          "text/html"
        );
        if (!hasBase) doc.head.insertAdjacentHTML("afterbegin", "<base href='/static/' />");
        if (isLanding && !hasYandex) setupYandexTag(doc);
        if (isLanding && !hasGoogleScrile) setupGoogleTag(doc);

        doc.head.querySelectorAll("[data-do-not-store=true]").forEach(n => n.remove());
        if (enabledHideLink) {
          doc.body.querySelectorAll(".scrile-link").forEach(n => n.remove());
        }
        const urlStore = editor.StorageManager.getConfig().urlStore;
        await axios.put(urlStore, doc.documentElement.outerHTML);
        clb();
      } catch (e) {
        console.error(e);
        editor.Modal.open({
          title: "Error",
          content: "Changes could not be saved due to internal error.",
        });
        clbErr(e);
      }
    },
  });
}
